import React, { useState, useEffect } from 'react';
import WeekendForecast from './WeekendForecast';
import ForecastTable from './ForecastTable';
import Navigation from './Navigation';
import Footer from './Footer'; // Footer importieren
import { fetchWeatherData, getFallbackData } from '../services/WeatherService';

/**
 * SunnyScorePage - Komponente zum Anzeigen der Wochenendvorhersage und 14-Tage-Prognose
 * @returns {JSX.Element} SunnyScore-Seite
 */
function SunnyScorePage() {
  const [wetterDaten, setWetterDaten] = useState(null);
  const [prognoseDaten, setPrognoseDaten] = useState([]);
  const [weekendDaten, setWeekendDaten] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [useFallback, setUseFallback] = useState(false);

  useEffect(() => {
    const loadWeatherData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Versuch die echten Daten abzurufen
        const { current, forecast, weekend } = await fetchWeatherData();
        setWetterDaten(current);
        setPrognoseDaten(forecast);
        setWeekendDaten(weekend);
        setUseFallback(false);
      } catch (err) {
        console.error('Fehler beim Laden der Wetterdaten:', err);
        setError(`Fehler beim Laden der Wetterdaten: ${err.message}`);
        
        // Fallback-Daten verwenden
        if (!useFallback) {
          const { current, forecast, weekend } = getFallbackData();
          setWetterDaten(current);
          setPrognoseDaten(forecast);
          setWeekendDaten(weekend);
          setUseFallback(true);
        }
      } finally {
        setLoading(false);
      }
    };

    loadWeatherData();

    // Automatisches Neuladen der Daten alle 30 Minuten
    const intervalId = setInterval(loadWeatherData, 30 * 60 * 1000);
    
    // Aufräumen beim Unmount der Komponente
    return () => clearInterval(intervalId);
  }, [useFallback]);

  const retryLoading = () => {
    setLoading(true);
    setError(null);
    // Die useEffect wird sich erneut ausführen, da wir useFallback ändern
    setUseFallback(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navigation />
      
      <div className="container mx-auto px-4 py-6 flex-grow">
        {/* Der gesamte Inhaltsbereich mit rosa Hintergrund, abgerundeten Ecken und Schatten */}
      <div className="bg-gradient-to-r from-rose-50 to-teal-50 p-8 rounded-2xl shadow-sm">
          {/* Header-Bereich */}
          <div className="py-6">
            <div className="container mx-auto px-4">
              <h1 className="text-3xl font-bold text-center">SunnyScore™</h1>
              <p className="mt-2 text-center text-gray-700">
                Ride on the sunny side with the SunnyScore™ by Sunny Ride.
              </p>
              <p className="mt-2 text-center text-gray-700">
                Der SunnyScore zeigt dir, wie gut die aktuellen Wetterbedingungen zum Fahrradfahren geeignet sind.
                Basierend auf historischen Daten des Fahrradaufkommens in Braunschweig.
              </p>
            </div>
          </div>
          
          {/* Inhaltsbereich */}
          <div className="px-4 py-6">
            <div className="container mx-auto">
              {loading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-rose-500"></div>
                  <div className="ml-4 text-lg text-gray-700">Lade Wetterdaten...</div>
                </div>
              ) : (
                <>
                  {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
                      <strong className="font-bold">Fehler:</strong>
                      <span className="block sm:inline"> {error}</span>
                      {useFallback && (
                        <div className="mt-2">
                          <p>Es werden Beispieldaten angezeigt.</p>
                          <button 
                            className="mt-2 bg-rose-500 hover:bg-rose-600 text-white font-bold py-1 px-3 rounded"
                            onClick={retryLoading}
                          >
                            Erneut versuchen
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {wetterDaten && (
                    <div className={useFallback ? "opacity-70" : ""}>
                      {/* Wochenendprognose anzeigen */}
                      <WeekendForecast weekendData={weekendDaten} />
                      
                      {/* 14-Tage-Vorhersage */}
                      <ForecastTable forecastData={prognoseDaten} />
                      
                      {/* Hinweis für Beispieldaten */}
                      {useFallback && (
                        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 text-amber-700 rounded-lg">
                          <p className="font-bold">Hinweis: Es werden Beispieldaten angezeigt.</p>
                          <p>Die Wetterdaten konnten nicht von der API abgerufen werden. 
                            Die angezeigten Daten dienen nur zu Demonstrationszwecken.</p>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Footer-Komponente einfügen */}
      <Footer />
    </div>
  );
}

export default SunnyScorePage;